import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	Collapse,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Svg from "../svg";
// hooks
import useStorage, { StorageKeys } from "../../hooks/useStorage";
// services
import { icons } from "@local/theme";
// types
import type { ReactNode } from "react";
import type { MenuItems, MenuItemSubMenu } from "../../types/menu";

type Props = Readonly<{
	activeRoute: string;
	item: MenuItemSubMenu;
	renderNestedMenu: (menuItems: MenuItems, nested: boolean, activeRoute: string) => ReactNode;
}>;

const NavigationNestedMenu = ({ activeRoute, item, renderNestedMenu }: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();

	const replaceData = useMemo(() => ({ MENU_ID: item.id }), [item.id]);
	const [menuOpen, setMenuOpen] = useStorage(StorageKeys.menu, replaceData);

	const active = item.items.some((subItem) => (subItem.type === "entry" && subItem.route === activeRoute));

	useEffect(() => {
		if (active) {
			setMenuOpen(active);
		}
	}, [active, setMenuOpen]);

	const IconElement = item.icon;

	return (
		<>
			{!item.singleDivider && <Divider />}
			<ListItemButton
				id={`main-menu-${item.id}`}
				style={{ color: menuOpen ? theme.vars.palette.text.primary : theme.vars.palette.text.secondary }}
				onClick={() => (setMenuOpen((prevOpen) => (!prevOpen)))}
			>
				{IconElement && <ListItemIcon><IconElement /></ListItemIcon>}
				<ListItemText>{t(item.label)}</ListItemText>
				<ListItemIcon className="ListItemIcon-right" style={{ display: "flex" }}>
					{menuOpen
						? <icons.ExpandLess htmlColor={theme.vars.palette.text.secondary} />
						: <Svg src="navigation/arrowDropDown.svg" color={theme.vars.palette.text.secondary} />
					}
				</ListItemIcon>
			</ListItemButton>
			<Collapse in={menuOpen} timeout="auto" unmountOnExit={true}>
				<List component="div" disablePadding={true}>
					{renderNestedMenu(item.items, true, activeRoute)}
				</List>
			</Collapse>
			<Divider />
		</>
	);
};

export default NavigationNestedMenu;
